import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { JamNight } from "@shared/schema";
import { FormModal } from "@/components/form-modal";
import { MutationButton } from "@/components/mutation-button";

const formSchema = z.object({
  timeLimit: z.coerce.number().int().min(0, "Time limit must be a positive number").optional().nullable(),
});

type FormData = z.infer<typeof formSchema>;

interface SetTimeLimitModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  jamNight: JamNight;
}

export default function SetTimeLimitModal({ isOpen, onOpenChange, jamNight }: SetTimeLimitModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      timeLimit: jamNight.timeLimit,
    },
  });

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      form.reset({ timeLimit: jamNight.timeLimit });
    }
  }, [isOpen, form, jamNight.timeLimit]);

  const setTimeLimitMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const payload = { 
        ...jamNight, 
        date: new Date(jamNight.date).toISOString(),
        timeLimit: data.timeLimit || null 
      };
      const response = await fetch(`/api/events/${jamNight.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error('Failed to set time limit');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/events/${jamNight.id}`] });
      toast({
        title: "Success",
        description: "Time limit updated successfully!",
      });
      onOpenChange(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to set time limit",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    setTimeLimitMutation.mutate(data);
  };

  const removeLimit = () => {
    form.setValue("timeLimit", null);
    setTimeLimitMutation.mutate({ timeLimit: null });
  };

  return (
    <FormModal
      open={isOpen}
      onOpenChange={onOpenChange}
      title="Set Time Limit"
      form={form}
      onSubmit={onSubmit}
      isLoading={setTimeLimitMutation.isPending}
      submitLabel={setTimeLimitMutation.isPending ? "Saving..." : "Save"}
    >
      <FormField
        control={form.control}
        name="timeLimit"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Time Limit (minutes)</FormLabel>
            <FormControl>
              <Input
                type="number"
                placeholder="Enter time limit in minutes"
                {...field}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <div className="flex justify-center pt-2">
        <MutationButton
          type="button"
          variant="destructive"
          onClick={removeLimit}
          isLoading={setTimeLimitMutation.isPending}
          loadingText="Removing..."
          glassMorphism={false}
        >
          Remove Limit
        </MutationButton>
      </div>
    </FormModal>
  );
} 